import { US_STATES } from '@ems/clinical';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, CheckCircle2 } from 'lucide-react';
import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { Alert, Button, Card, Field, Input, PageHeader, QueryError, Select, Spinner } from '@/components/ui';
import { useAuth } from '@/lib/auth';
import { db, unwrap } from '@/lib/db';
import { errorMessage, ORG_TYPE_LABELS, ROLE_LABELS, slugify } from '@/lib/utils';
import type { Organization } from '@/types/db';

export function CreateOrgPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { refresh, setActiveOrgId } = useAuth();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [type, setType] = useState('ems_agency');
  const [state, setState] = useState('');

  const create = useMutation({
    mutationFn: async () =>
      unwrap(await db.rpc('create_organization', { p_name: name, p_slug: slug, p_type: type, p_state_code: state || null })) as Organization,
    onSuccess: async (org) => {
      await refresh();
      setActiveOrgId(org.id);
      queryClient.invalidateQueries();
      navigate(`/admin/${org.id}`);
    },
  });

  return (
    <div className="mx-auto max-w-xl">
      <PageHeader back={{ to: '/profile', label: 'Profile' }} title="Create an organization" subtitle="You’ll become the owner and can invite providers, educators, and your medical director." />
      <Card className="p-4">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <Field label="Organization name">
            <Input
              value={name}
              required
              minLength={2}
              maxLength={120}
              onChange={(e) => {
                setName(e.target.value);
                if (!slugEdited) setSlug(slugify(e.target.value));
              }}
              placeholder="Metro County EMS"
            />
          </Field>
          <Field label="Short name (URL)" hint="Lowercase letters, numbers, and dashes">
            <Input
              value={slug}
              required
              pattern="[a-z0-9]([a-z0-9-]{0,62}[a-z0-9])?"
              onChange={(e) => {
                setSlugEdited(true);
                setSlug(e.target.value.toLowerCase());
              }}
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Type">
              <Select value={type} onChange={(e) => setType(e.target.value)}>
                {Object.entries(ORG_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="State" hint="Determines which state library protocols you inherit">
              <Select value={state} onChange={(e) => setState(e.target.value)}>
                <option value="">National only</option>
                {US_STATES.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          {create.isError && <Alert tone="danger">{/duplicate key/.test(errorMessage(create.error)) ? 'That short name is already taken.' : errorMessage(create.error)}</Alert>}
          <Button type="submit" size="lg" className="w-full" loading={create.isPending} icon={<Building2 className="size-5" />}>
            Create organization
          </Button>
        </form>
      </Card>
    </div>
  );
}

export function JoinOrgPage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { refresh, setActiveOrgId, user, ready } = useAuth();
  const preview = useQuery({
    queryKey: ['invite', token],
    enabled: Boolean(user),
    queryFn: async () => {
      const rows = unwrap(await db.rpc('get_invite_preview', { p_token: token })) as { org_id: string; org_name: string; org_type: string; role: string; valid: boolean }[];
      return rows[0] ?? null;
    },
  });
  const accept = useMutation({
    mutationFn: async () => unwrap(await db.rpc('accept_org_invite', { p_token: token })) as string,
    onSuccess: async (orgId) => {
      await refresh();
      setActiveOrgId(orgId);
      queryClient.invalidateQueries();
      navigate('/');
    },
  });

  if (!ready) return <Spinner />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(`/join/${token}`)}`} replace />;
  if (preview.isPending) return <Spinner />;
  if (preview.isError) return <QueryError error={preview.error} />;

  return (
    <div className="mx-auto max-w-md">
      <Card className="p-6 text-center">
        {!preview.data || !preview.data.valid ? (
          <Alert tone="danger" title="Invitation unavailable">
            This invitation link is invalid, expired, or has already been used. Ask your administrator for a new link.
          </Alert>
        ) : (
          <>
            <Building2 className="mx-auto mb-3 size-12 text-stone-400 dark:text-stone-500" />
            <h1 className="text-xl font-semibold">Join {preview.data.org_name}</h1>
            <p className="mt-1 text-stone-500">
              {ORG_TYPE_LABELS[preview.data.org_type]} · you’ll join as <strong>{ROLE_LABELS[preview.data.role]}</strong>
            </p>
            {accept.isError && <Alert tone="danger" className="mt-4 text-left">{errorMessage(accept.error)}</Alert>}
            <Button size="lg" className="mt-6 w-full" loading={accept.isPending} icon={<CheckCircle2 className="size-5" />} onClick={() => accept.mutate()}>
              Accept invitation
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}
